// The H3 resolution ladder: how a zoom level maps onto an H3 resolution, and the
// offset an operator may apply on top of it.
//
// It is pure, and it lives outside the lab page that first wrote it, because
// three surfaces now read the same arithmetic and must agree: the lab's density
// grids (each rung is its own tile source), the workbench's saved layers (their
// tile URL carries the resolution), and the Planning workbench's authoring grid
// (which clicks cells). Nothing here knows about Vue, the store or the map — the
// offset is an argument, so the resolution a cell was cut at and the resolution
// a readout names stay one number instead of two copies of one rule.

/**
 * The BASE resolution ladder for the server grid. Each rung is its own tile
 * source (a resolution is a client-owned tile parameter, so two resolutions are
 * simply two URLs); the layer's zoom band decides which one is drawn, and
 * MapLibre does not load a source whose only layers are outside the current
 * zoom.
 *
 * One H3 step per two zoom levels, from res 8 at the district view all the way
 * to H3's maximum (res 15). Two zooms per step is the whole of H3's range spent
 * evenly over the lab's whole zoom range, and it is what keeps the hexagons from
 * blowing up: H3's hexagon shrinks by √7 ≈ 2.65 per step while two zooms
 * multiply the screen scale by 4, so a fixed resolution grows 4× per two zooms —
 * this ladder grows 4/2.65 ≈ 1.5× per two zooms, and within one band (two zoom
 * levels, one doubling) a cell is never more than 2× its band's smallest. (The
 * ladder used to flatten at z13 with res 10 on its last rung, so a cell grew 2×
 * per zoom all the way to z22 — ~2200 px across at z20, where this ladder's is
 * 45 and the default offset's is 118.)
 *
 * The first three rungs are pinned to the ladder the operator has been reading:
 * the district view is z9–10 (its full extent) and the default offset, −1, has
 * to leave exactly the resolutions that were on screen before the offset
 * existed — res 7 at z≤8, 8 at z9–10, 9 at z11–12 and 10 at z13–14. The
 * refinement this ladder adds is therefore all at z15 and beyond, which is
 * exactly where the old one had stopped refining.
 */
export const H3_LADDER: { maxZoom: number; res: number }[] = [
	{ maxZoom: 8, res: 8 },
	{ maxZoom: 10, res: 9 },
	{ maxZoom: 12, res: 10 },
	{ maxZoom: 14, res: 11 },
	{ maxZoom: 16, res: 12 },
	{ maxZoom: 18, res: 13 },
	{ maxZoom: 20, res: 14 },
	{ maxZoom: 22, res: 15 },
];

/** H3's own resolution limits. Every effective resolution is clamped into this
 * range — the offset can ask for less than 0 or more than 15 on the end rungs,
 * which is not a resolution at all. */
export const H3_RES_MIN = 0;
export const H3_RES_MAX = 15;

/** The operator's ladder offset, in H3 steps. 0 is the base ladder above; −1 is
 * one step coarser everywhere, which is the ladder the lab drew before this
 * control existed. */
export const LADDER_OFFSET_MIN = -3;
export const LADDER_OFFSET_MAX = 3;
export const LADDER_OFFSET_DEFAULT = -1;

/** H3's average hexagon edge length at resolution 0, in metres, and the linear
 * shrink one step applies (√7). H3's published average edge lengths — 1281.256
 * km at r0, 483.056 km at r1, … 200.786 m at r9, 0.585 m at r15 — all sit on
 * this one curve, so the table is a formula rather than 16 constants. */
const H3_EDGE_RES0_M = 1_281_256;
const H3_EDGE_STEP = Math.sqrt(7);

/** The Web Mercator world circumference and the lab's tile size: a z-tile is
 * 512 px wide at every latitude, which is what turns a ground measure into a
 * screen measure. */
const MERIDIAN_CIRCUMFERENCE_M = 40_075_016.686;
const LADDER_TILE_PX = 512;

/** The district's mid-latitude — the lab's primary region, and the smaller of
 * the two cosines (the population mirror is ~2.6° north), so a cell-size rule
 * evaluated here is the conservative one: the same hexagon covers fewer px at
 * 36.24° than at 38.9°, and the rule binds where the district is read. */
const LADDER_LAT = 36.24;
const LADDER_COS_LAT = Math.cos((LADDER_LAT * Math.PI) / 180);

/** The coarsest zoom the ladder is read at: the district fills the pane at
 * z9–10 and below z8 the whole region is a few pixels, where a hexagon's size
 * stopped being a decision. It floors the cell-size cap (the first rung's band
 * starts at z0, where every resolution is sub-pixel and a cap would forbid the
 * operator's own default) and it is the zoom the control reports the first
 * band's px at. */
export const LADDER_FIRST_DETAIL_ZOOM = 8;

/** How wide one cell of a resolution is on screen, in px, at a zoom: the
 * hexagon's ground width — √3 × H3's average edge length, the width across the
 * flats, which is the visible width of a cell — over the ground measure of one
 * pixel, which is the Web Mercator tile at that zoom divided by the tile size. */
export function hexPxAcross(res: number, zoom: number): number {
	const metersPerPx =
		(MERIDIAN_CIRCUMFERENCE_M * LADDER_COS_LAT) / (LADDER_TILE_PX * 2 ** zoom);
	const acrossM = (Math.sqrt(3) * H3_EDGE_RES0_M) / H3_EDGE_STEP ** res;
	return acrossM / metersPerPx;
}

/**
 * The smallest a hexagon may be drawn at, in screen px. Half a pixel is four
 * tile-extent units (the tile is 512 px over an extent of 4096), the measure
 * ST_AsMVTGeom actually works in.
 */
const LADDER_MIN_CELL_PX = 0.5;

/**
 * The finest resolution the ladder may ask the tile server for at a zoom: the
 * finest one whose hexagon still covers `LADDER_MIN_CELL_PX` of a screen pixel.
 *
 * This is a guard, not a taste: the provider hands each cell to PostGIS's
 * ST_AsMVTGeom, which drops a polygon that collapses at the tile's integer
 * coordinates. Measured on this district's own z9 tile (tile-buffer 64, extent
 * 4096): res 11 — 3.2 extent units across — keeps every cell, res 12 (1.2
 * units) already loses 2.4% of them, res 13 (0.46 units) loses 87%, and res 14
 * and 15 come back as an EMPTY tile. Half a pixel is ~4 units, a 3× margin over
 * the last measurement that was complete; below it the grid is a wash that
 * quietly lacks hexagons.
 */
export function maxResForZoom(zoom: number): number {
	for (let res = H3_RES_MAX; res > H3_RES_MIN; res--) {
		if (hexPxAcross(res, zoom) >= LADDER_MIN_CELL_PX) return res;
	}
	return H3_RES_MIN;
}

/** What stopped a rung's resolution from being the one the offset asked for:
 * `cell` is the per-zoom cell-size cap above, `h3` is H3's own maximum. */
export type LadderLimit = "none" | "cell" | "h3";

/** One rung of the ladder the map actually draws. */
export interface LadderRung {
	/** The rung's zoom band is [minZoom, maxZoom], inclusive. */
	minZoom: number;
	maxZoom: number;
	/** The base ladder's own resolution, before the offset. */
	baseRes: number;
	/** The resolution the offset asked for, clamped to H3's range. */
	askedRes: number;
	/** The resolution drawn. */
	res: number;
	limit: LadderLimit;
}

/**
 * The ladder for one offset: every base rung shifted by `offset` steps, clamped
 * to H3's range, then capped by the zoom's cell-size guard. Adjacent rungs that
 * come out on the SAME resolution are merged into one band — two rungs of one
 * resolution would be two sources and two sets of layer ids for one grid (and
 * MapLibre refuses a duplicated source id), while one band is exactly what the
 * cap means: "from here on, the grid cannot get finer".
 *
 * The cap is evaluated at the band's FIRST zoom, because a band holds one
 * resolution and a cell is smallest on screen at its lowest zoom — so the cap
 * that holds there holds for the whole band, and `hexPxAcross` never falls
 * under a pixel at any zoom the ladder is read at. The floor is the ladder's own
 * first detail zoom: the first band starts at z0, where every resolution the
 * ladder can draw is sub-pixel and the district is a few pixels across, and a
 * cap evaluated there would forbid the operator's own default.
 */
export function ladderForOffset(offset: number): LadderRung[] {
	const rungs: LadderRung[] = [];
	H3_LADDER.forEach((base, index) => {
		const minZoom = index === 0 ? 0 : H3_LADDER[index - 1].maxZoom + 1;
		const wanted = base.res + offset;
		const askedRes = Math.min(H3_RES_MAX, Math.max(H3_RES_MIN, wanted));
		const res = Math.min(
			askedRes,
			maxResForZoom(Math.max(minZoom, LADDER_FIRST_DETAIL_ZOOM)),
		);
		const limit: LadderLimit =
			res < Math.min(wanted, H3_RES_MAX)
				? "cell"
				: wanted > H3_RES_MAX
					? "h3"
					: "none";
		const last = rungs[rungs.length - 1];
		if (last && last.res === res) {
			last.maxZoom = base.maxZoom;
			// A merged band is limited if either of the rungs it covers was: the
			// reason it cannot get finer applies somewhere inside it.
			if (limit !== "none" && last.limit === "none") last.limit = limit;
			return;
		}
		rungs.push({
			minZoom,
			maxZoom: base.maxZoom,
			baseRes: base.res,
			askedRes,
			res,
			limit,
		});
	});
	return rungs;
}

/**
 * The resolution a zoom draws on the ladder — the base rung's resolution with
 * `offset` applied, H3's clamp and the zoom's cell-size cap included. A zoom
 * past the last rung reads the last rung.
 *
 * The offset is a REQUIRED argument, not a hidden global, for the same reason
 * `cellAt` takes its resolution: every reader of this — a tile URL, a hit-test,
 * a readout — passes the one offset it already holds, so no caller can report a
 * resolution for a grid that was cut at another. It is the same walk
 * `ladderForOffset` does, so a single rung of that ladder cannot disagree with
 * the number this answers.
 */
export function h3ResForZoom(zoom: number, offset: number): number {
	const rungs = ladderForOffset(offset);
	const rung =
		rungs.find((entry) => zoom <= entry.maxZoom) ?? rungs[rungs.length - 1];
	return rung.res;
}

// ── The operator's offset ──────────────────────────────────────────────────

/** An H3 resolution OFFSET as the workbench's resolution control stores it: a
 * whole number clamped to the lab's ladder (`LADDER_OFFSET_MIN`..
 * `LADDER_OFFSET_MAX`), which is the same rung arithmetic the lab's slider uses.
 * One offset governs every H3 layer at once, so the saved layers stay comparable
 * cell to cell. A value that is not a number resolves to the ladder's default
 * rather than reaching the tile server as NaN. */
export function clampH3Offset(offset: number): number {
	if (!Number.isFinite(offset)) return LADDER_OFFSET_DEFAULT;
	return Math.min(
		LADDER_OFFSET_MAX,
		Math.max(LADDER_OFFSET_MIN, Math.round(offset)),
	);
}

// ── The authoring grid's floor ─────────────────────────────────────────────

/** The smallest a cell may be drawn at, in screen px, in a grid the operator
 * CLICKS. 22 px is the conventional minimum touch target: below it a cell is not
 * something a click can reliably land on, which is the whole point of the grid. */
export const AUTHORING_MIN_CELL_PX = 22;

/**
 * The resolution a CELL-EDITING grid uses at a zoom: the same `hexPxAcross`
 * curve `maxResForZoom` walks, floored at a clickable cell instead of at the
 * tile renderer's guard.
 *
 * The two floors answer different questions, which is why they are two
 * constants. `maxResForZoom` exists because the server hands every cell to
 * PostGIS's `ST_AsMVTGeom`, which drops a polygon that collapses at the tile's
 * integer coordinates — a constraint irrelevant to a grid the CLIENT generates,
 * where no geometry is ever rasterised server-side and half a pixel draws
 * perfectly well. Here the binding constraint is the operator's aim: a cell
 * smaller than a fingertip is not a target you can hit, so the grid stops
 * refining and lets the cells grow. Every value is in `H3_RES_MIN`..
 * `H3_RES_MAX` by construction — the walk starts at H3's finest and never
 * passes its coarsest.
 */
export function authoringResForZoom(zoom: number): number {
	for (let res = H3_RES_MAX; res > H3_RES_MIN; res--) {
		if (hexPxAcross(res, zoom) >= AUTHORING_MIN_CELL_PX) return res;
	}
	return H3_RES_MIN;
}
